// ── Record Payment ──────────────────────────────────────────────
//
// MOVED OUT OF app.js UNCHANGED (standing rule ③: extract what you touch, and
// MOVE and CHANGE are separate commits). Everything below the deps block is the
// app.js text as it stood: the same element ids, the same words, the same
// window.* names the modal's inline handlers in index.html call.
//
// app.js keeps four things this needs, handed over with setRecordPaymentDeps:
// its own `clients` array (the VISIBLE set, not state.js's), periodLabel,
// getOverdueMonthCount and renderManualContinuity, and the signed-in user.

import * as _config from './config.js';
import * as _sessions from './sessions.js';
import { escHtml, showToast, formatDate, formatCurrency, getNextContinuityDueDate, isManualContinuity } from './utils.js';
import { nextInstallmentNumber, installmentsByPlan, asksForTheMonth } from './payment-rows.js';
import { updateClient } from './clients.js';
import { callFunction } from './api.js';
import { renderCurrentView } from './navigation.js';
import { upgradeDates } from './date-picker.js';

/** @type {{ getClients: () => any[], periodLabel: (d: any) => string, getOverdueMonthCount: (c: any) => number, renderManualContinuity: () => void, getCurrentUser: () => any }} */
let _deps = { getClients: () => [], periodLabel: () => '', getOverdueMonthCount: () => 0, renderManualContinuity: () => {}, getCurrentUser: () => null };

/** @param {Partial<typeof _deps>} d */
export function setRecordPaymentDeps(d) { _deps = { ..._deps, ...d }; }

/** app.js's own roster array — see the header. */
const clientsList = () => _deps.getClients() || [];

/** How the window was opened: 'continuity', 'log' (+ Add Entry) or a plain payment. */
let _mode = null;

/** The window's title and button: + Add Entry's window says Add Entry, so its note's "use + Record Payment" never
 *  points at the button under it (Clayton, 2026-10-15). */
const actionWords = () => (_mode === 'log' ? 'Add Entry' : 'Record Payment');

// ── The month a payment gives (one sessions system, part 7) ──────────
// Where the account's switch is on, the server gives the month a cash, check or
// Venmo payment buys, from the Payment History row this window just wrote, and
// decides everything else (which clients, installments included). The coach is
// told what it added, or why nothing was.

/** 'Nov 5' @param {string} ymd */
const shortDay = (ymd) => new Date(`${ymd}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

const WHY_NONE = {
  'waits-for-stripe':   "This client's sessions come with their card payments.",
  'waits-for-schedule': "This client's sessions are added on their billing day each month.",
  overlaps:             'A month of sessions already covers that date.',
  ends:                 "That date is after the client's package ends.",
};

/**
 * What the coach is told about coach-session-month-paid's answer.
 * @param {any} a the answer
 * @returns {string|null}
 */
export function monthPaidWords(a) {
  if (!a) return null;
  const span = a.periodStart && a.periodEnd ? `${shortDay(a.periodStart)} to ${shortDay(a.periodEnd)}` : '';
  if (a.issued) return `${a.qty} session${a.qty === 1 ? '' : 's'} added, ${span}.`;
  if (a.alreadyIssued) return span ? `The sessions for ${span} were already added.` : 'The sessions for this payment were already added.';
  const why = /** @type {Record<string, string>} */ (WHY_NONE)[a.reason]
    || (a.reason === 'no-month' && a.detail === 'no-sessions' ? "This client's package has no sessions number." : null);
  return why ? `No sessions were added. ${why}` : 'No sessions were added for this payment.';
}

/**
 * Ask the server for the month this payment row gives, once more on Try again (it gives a row's month once however
 * often it is asked). Resolves to the words for the coach; rejects when the server could not be reached.
 * @param {any} c the client @param {string} date @param {string} paymentRowId @param {boolean} [continuity]
 */
async function giveTheMonth(c, date, paymentRowId, continuity = false) {
  if (!(await _sessions.sessionsLedgerOn())) {
    // Read as off on Try again: the page's own way, as Record Payment always did (never for installments, and never for
    // a continuity payment: switched off, those months still come on schedule; the final review, 10/30).
    if (!continuity && c.billingStructure !== 'installments') await _sessions.autoIssueForPaymentDate(c, _deps.getCurrentUser()?.id, date);
    return 'The sessions for this payment are up to date.';
  }
  const out = await _sessions.autoIssueForPaymentDate(c, _deps.getCurrentUser()?.id, date, { paymentRowId });
  if (out && out.issued) void _sessions.refreshSessionData();
  return monthPaidWords(out);
}

/** The month did not arrive: say so, and offer to ask again for the same row. */
// ⚠️ LONG ENOUGH TO SEE (review 10/12): a message with a button lasts 9 seconds, and after it Record Payment has no
// other way to ask for this payment's month (recording it again writes a second payment). Five minutes, and the daily
// check reports a payment whose month never reached the server.
const TRY_AGAIN_MS = 5 * 60 * 1000;

/** A refusal asking again cannot fix (a 4xx with the server's words), or null for one worth another try. @param {any} err */
const lasting = (err) => (err && err.status >= 400 && err.status < 500 && err.payload && typeof err.payload.error === 'string' ? err.payload.error : null);

function offerTryAgain(/** @type {string} */ lead, /** @type {any} */ c, /** @type {string} */ date, /** @type {string} */ paymentRowId, continuity = false) {
  showToast(`${lead}The sessions for it were not added yet.`, 'warning', {
    actionLabel: 'Try again',
    duration: TRY_AGAIN_MS,
    onAction: () => {
      giveTheMonth(c, date, paymentRowId, continuity).then(
        (words) => showToast(words || 'No sessions were added for this payment.', 'success'),
        (err) => {
          const words = lasting(err);
          if (words) showToast(`Its sessions were not added: ${words}`, 'error');
          else offerTryAgain('', c, date, paymentRowId, continuity);
        },
      );
    },
  });
}

/** What the window is recording, under its title: a label over each value, on a soft panel. @param {string} cells */
const facts = (cells) => `<div class="rp-facts">${cells}</div>`;
/** @param {string} label @param {string} valueHtml already escaped @param {boolean} [warn] */
const fact = (label, valueHtml, warn = false) => `<div class="rp-fact${warn ? ' rp-fact--warn' : ''}"><span class="rp-fact-k">${label}</span><span class="rp-fact-v">${valueHtml}</span></div>`;

/**
 * Where a continuity payment came from: continuity recorded here is paid by hand (a Stripe continuity collects
 * itself), so a client who paid their program by card is never written down as paying Stripe (final review 10/19).
 * @param {any} c
 */
const handSource = (c) => (!c.paymentSource || c.paymentSource === 'stripe' ? 'external' : c.paymentSource);

const SOURCE_WORDS = { venmo: 'Venmo', zelle: 'Zelle', cash: 'Cash', check: 'Check', affirm: 'Affirm', stripe: 'Stripe' };

// ── Installments (Clayton 10/22, spec 2026-10-22 §4.3, §4.6) ─────────
// Record payment on an installment opens this window filled in with that payment, its amount and description locked,
// and saves it only through the server, by the payment's id (never the page's whole-record write, never the month
// door). + Record Payment finds the payment from the Billing card's read: the next one waiting that is paid by hand.

export { asksForTheMonth };

/** The payment this window records, when it was opened on one. @type {{ payment: any, count: number, requestKey: string }|null} */
let _inst = null;
/** Review C fix 2: a client not paying monthly (fix-check C P15) whose only payments open are by card: a plain payment, never one of those. */
let _cardsOnly = false;
/** Counts every opening and closing: a read that lands after the window was opened again, or closed, does nothing. */
let _openings = 0;
/** The Billing card's module, loaded once when first asked for (its read and the window's look); a failed load is tried again. @type {Promise<any>|null} */
let _card = null;
const cardModule = () => (_card ||= import('./change-billing/billing-card.js').catch((err) => { _card = null; throw err; }));

/** A client whose payments the read must find: a flagged record, a program waiting, or a payment already recorded. @param {any} c */
const onInstallments = (c) => installmentsByPlan(c) || !!c.programWaiting
  || (Array.isArray(c.paymentHistory) && c.paymentHistory.some((/** @type {any} */ r) => r && r.installment));
/** Ruling 2: a client who still pays monthly by hand keeps the monthly window; the installment waits under Coming up. @param {any} c */
const paysMonthlyByHand = (c) => c.billingStructure === 'MRR' && ['cash', 'check', 'venmo', 'zelle'].includes(c.paymentSource);

window.openRecordPaymentModal = function(clientId, mode, opts) {
  const c = clientsList().find(x => x.id === clientId);
  if (!c) return;
  const opening = ++_openings;
  fillWindow(c, mode || null, opts);
  if (mode) { showWindow(); return; }
  return findThePayment(c, opening);
};

/**
 * + Record Payment: read the client's payments, then open on the next one to record, or the usual window (with a line
 * for a client who pays monthly by hand). A read that fails, for a client on installments, opens nothing.
 * @param {any} c @param {number} opening
 */
async function findThePayment(c, opening) {
  /** @type {{ payment: any, count: number }|null} */
  let next = null;
  /** Installments step 4 (Call C8): what the card payments still open say, when none by hand is ('' none) */
  let cardsLeft = '';
  let read = false;
  // A read refused for want of Take payment (403): asking again never works, so it is said (the final review, 10/22)
  let refused = false;
  try {
    const { readBillingView } = await cardModule();
    const view = await readBillingView(c.id);
    const { nextOpenPayment } = await import('./change-billing/plan-answers.js');
    const groups = view && Array.isArray(view.installments) ? view.installments : [];
    next = nextOpenPayment(groups);
    cardsLeft = cardsLeftWords(groups);
    read = true;
  } catch (err) {
    refused = /** @type {any} */ (err).status === 403;
    console.warn('[record-payment] their payments could not be read:', /** @type {any} */ (err).message);
  }
  if (opening !== _openings) return;
  if (!read && onInstallments(c)) {
    showToast(refused ? "You don't have permission to record payments. Ask the account owner." : "Couldn't read their payments. Try again.", 'error');
    return;
  }
  if (next && paysMonthlyByHand(c)) {
    document.getElementById('rp-context-bar')?.insertAdjacentHTML('beforeend', '<p class="rp-inst-note" id="rp-inst-note">Recording an installment? Use Record payment on it under Coming up.</p>');
  } else if (next) {
    fillWindow(c, 'installment', next);
  } else if (cardsLeft) {
    // Call C8: only card payments open: the usual window, with one line saying so (never a second row for one of them);
    // review C fix 2: drawn without the next installment, so what it saves is a plain payment
    fillWindow(c, null, { cardsOnly: true });
    const note = document.createElement('p');
    note.className = 'rp-inst-note';
    note.id = 'rp-inst-note';
    note.textContent = cardsLeft;
    document.getElementById('rp-context-bar')?.appendChild(note);
  }
  showWindow();
}

/**
 * Installments step 4 (Call C8; r-follows.md 3.5 trap 1): the card payments still open across every program, in words
 * for the usual window: the ones still to come are charged on their days, and one that didn't go through is in Amount
 * owed, where Paid another way records it. '' when there are none. Review C fix 2: one still to come that isn't set to
 * charge (Coming up's notCharged, cardGone, notSetUp, in its order) is named, with what to use on it there, and then
 * none is said to be charged on its day. Fix-check C P14: payments a sentence names from two programs (an older one kept
 * after End coaching partway) get no numbers, since both have a payment 2.
 * @param {any[]} groups InstallmentsView[] @returns {string}
 */
function cardsLeftWords(groups) {
  const open = groups.flatMap((/** @type {any} */ g, /** @type {number} */ i) => (g.payments || []).map((/** @type {any} */ p) => ({ ...p, program: i })))
    .filter((/** @type {any} */ p) => p.way === 'card');
  const waiting = open.filter((/** @type {any} */ p) => p.state === 'waiting');
  const notCharged = waiting.filter((/** @type {any} */ p) => p.notCharged);
  const stops = [
    // the server's own words for it (coach-billing-plan.js setUpCardPayments), another card when theirs is gone; from two
    // programs, Change on them (each row says to pick another day or card)
    ...(programsOf(notCharged) > 1 ? [stopWords(notCharged, '', "weren't charged", 'Change')]
      : notCharged.map((/** @type {any} */ p) => stopWords([p], "wasn't charged", '', 'Change', ` to pick another ${p.cardGone ? 'card' : 'day'}`))),
    stopWords(waiting.filter((/** @type {any} */ p) => !p.notCharged && p.cardGone), 'is on a card no longer on their file', 'are on a card no longer on their file', 'Change', ' to pick another card'),
    stopWords(waiting.filter((/** @type {any} */ p) => !p.notCharged && !p.cardGone && p.notSetUp), "isn't set up to charge yet", "aren't set up to charge yet", 'Set it up'),
  ].filter(Boolean);
  return [!waiting.length ? '' : stops.length ? 'The installments still to come are by card.' : 'The installments still to come are by card. Each is charged on its day.', ...stops,
    open.some((/** @type {any} */ p) => p.state === 'failed') ? "A card payment that didn't go through is in Amount owed. Record it there with Paid another way." : '']
    .filter(Boolean).join(' ');
}

/**
 * Review C fix 2: "Payment 3 isn't set up to charge yet. Use Set it up on it under Coming up." ("Payments 2 and 3 aren't
 * ... on them ..."), '' for none. Fix-check C P14: from two programs, "Some card payments aren't set up to charge yet. Use
 * Set it up on them under Coming up."
 * @param {any[]} ps @param {string} one @param {string} many @param {string} use @param {string} [then]
 */
function stopWords(ps, one, many, use, then = '') {
  if (!ps.length) return '';
  if (programsOf(ps) > 1) return `Some card payments ${many}. Use ${use} on them under Coming up${then}.`;
  const ns = ps.map((p) => p.n);
  const who = ns.length === 1 ? `Payment ${ns[0]} ${one}` : `Payments ${ns.slice(0, -1).join(', ')} and ${ns[ns.length - 1]} ${many}`;
  return `${who}. Use ${use} on ${ns.length === 1 ? 'it' : 'them'} under Coming up${then}.`;
}

/** Fix-check C P14: how many programs these payments come from (cardsLeftWords tags each). @param {any[]} ps */
const programsOf = (ps) => new Set(ps.map((p) => p.program)).size;

/** @param {any} c @param {string|null} mode @param {any} [opts] the installment's payment and count, or { cardsOnly } (review C fix 2) */
function fillWindow(c, mode, opts) {
  const clientId = c.id;
  const isContinuity = mode === 'continuity';
  _mode = mode;
  _inst = mode === 'installment' && opts && opts.payment
    ? { payment: opts.payment, count: opts.count, requestKey: crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) } : null;
  // Fix-check C P15: a paid in full or comped client whose card installments wait too; one who pays monthly keeps that window
  _cardsOnly = !mode && !!(opts && opts.cardsOnly) && c.billingStructure !== 'MRR';
  const today = _config.localToday();
  const sourceLabel = SOURCE_WORDS[c.paymentSource] || c.paymentSource;

  let defaultAmount, defaultDesc, contextHtml;

  if (_inst) {
    const p = _inst.payment;
    defaultAmount = p.amountCents % 100 ? (p.amountCents / 100).toFixed(2) : String(p.amountCents / 100);
    defaultDesc   = `Payment ${p.n} of ${_inst.count}`;
    contextHtml = facts(`
      ${fact('Billing', 'Installments')}
      ${fact('Payment', `${p.n} of ${_inst.count}`)}
      ${fact('Source', escHtml(SOURCE_WORDS[p.method] || p.method || ''))}
      ${fact('Due', escHtml(p.when))}`);
  } else if (isContinuity) {
    defaultAmount = c.continuityRate || '';
    defaultDesc   = 'Continuity Payment';
    const dueDate       = getNextContinuityDueDate(c);
    const overdueMonths = _deps.getOverdueMonthCount(c);
    const duePeriod     = dueDate ? _deps.periodLabel(dueDate) : '';
    contextHtml = facts(`
      ${fact('Type', 'Manual Continuity')}
      ${fact('Source', escHtml(handSource(c) === 'external' ? 'By hand' : sourceLabel))}
      ${fact('Monthly Rate', `${formatCurrency(c.continuityRate)}/mo`)}
      ${dueDate ? fact('Period', duePeriod) : ''}
      ${dueDate ? fact('Due Date', formatDate(_config.localDateStr(dueDate))) : ''}
      ${overdueMonths > 1 ? fact('Overdue', `${overdueMonths} months · ${formatCurrency(overdueMonths * (Number(c.continuityRate) || 0))} total owed`, true) : ''}`);
  } else if (mode === 'log') {
    // Historical / corrective entry — does not affect billing schedule or lastContinuityCollectedDate.
    // Period badge is derived from the date the user enters.
    defaultAmount = c.continuityRate || c.mrrAmount || '';
    defaultDesc   = isManualContinuity(c) ? 'Continuity Payment'
                    : c.billingStructure === 'installments' ? 'Installment Payment' : 'Payment';
    // ⚠️ The way to record a real payment is + Record Payment in the Financial Summary; there was never a Mark Paid
    // button at the top of the profile (Clayton, 2026-10-15)
    contextHtml = facts(`
      ${fact('Type', 'Ledger Adjustment')}
      ${fact('Source', escHtml(sourceLabel))}`) + `
      <div class="rp-note" role="note">
        <svg class="rp-note-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
        <div>
          <strong class="rp-note-h">Only for fixing Payment History</strong>
          <ul>
            <li>Adds a row and changes Total Collected.</li>
            <li>Doesn't mark anything paid or change the next due date.</li>
            <li>Client paid you? Close this and use <strong>+ Record Payment</strong> in the Financial Summary.</li>
          </ul>
        </div>
      </div>`;
  } else {
    const existingHist  = Array.isArray(c.paymentHistory) ? c.paymentHistory : [];
    const totalInst     = parseInt(c.installmentCount) || 0;
    const nextInstNum   = nextInstallmentNumber(existingHist, c);
    // Review C fix 2: with only card payments open, nothing of the next installment (a card charges it on its day)
    defaultAmount = _cardsOnly ? '' : c.billingStructure === 'installments' ? (c.mrrAmount || '') : (c.mrrAmount || '');
    defaultDesc   = _cardsOnly ? 'Payment' : c.billingStructure === 'installments' ? 'Installment Payment' : 'MRR Payment';
    // Change billing 4c part 3b PR 4 (Clayton 10/20, design note 15): while a monthly client's new program waits, a month
    // paid now would start on the program's first day: when it starts the day after the month they paid for ends (the
    // server marks it; on a later day a month paid now starts before it). A warning only: saving is still allowed
    const waits = c.billingStructure === 'MRR' && c.programWaiting && c.programWaiting.afterPaidMonth && Number(c.programWaiting.startsAt) > 0 ? c.programWaiting : null;
    const waitWords = waits ? `${c.firstName || String(c.name || 'Their').split(' ')[0]}'s new program starts on ${new Date(Number(waits.startsAt) * 1000)
      .toLocaleDateString('en-US', { month: 'long', day: 'numeric', ...(Number(waits.startsAt) * 1000 - Date.now() >= 300 * 86400000 ? { year: 'numeric' } : {}) })}. A month paid now would start the same day.` : '';
    contextHtml = facts(`
      ${fact('Billing', c.billingStructure === 'installments' ? 'Installments' : 'MRR')}
      ${fact('Source', escHtml(sourceLabel))}
      ${c.billingStructure === 'MRR'
        ? `${fact('Monthly Amount', `${formatCurrency(c.mrrAmount)}/mo`)}
           ${fact('Period', _deps.periodLabel(today))}`
        : _cardsOnly ? ''
        : `${fact('Installment', totalInst ? `${nextInstNum} of ${totalInst}` : String(nextInstNum))}
           ${fact('Amount', formatCurrency(c.mrrAmount))}`}
      ${waits ? fact('New program', escHtml(waitWords), true) : ''}`);
  }

  const bar = document.getElementById('rp-context-bar');
  if (bar) bar.innerHTML = contextHtml;
  // The client's name under the title, as the Change billing window has it (it was the first box of the band)
  const sub = document.getElementById('rp-sub');
  if (sub) sub.textContent = c.name || [c.firstName, c.lastName].filter(Boolean).join(' ');

  const overlay = document.getElementById('record-payment-overlay');
  // The CRM's own calendar for Date Received; the box shows the date the window sets (its change event relabels it)
  if (overlay) upgradeDates(overlay);
  const dateBox = /** @type {HTMLInputElement|null} */ (document.getElementById('rp-date'));

  // Installments: the payment's amount and words are locked, and the day is today or earlier
  const amountBox = /** @type {HTMLInputElement} */ (document.getElementById('rp-amount'));
  amountBox.value = defaultAmount;
  amountBox.readOnly = !!_inst;
  if (dateBox) {
    if (_inst) dateBox.max = today; else dateBox.removeAttribute('max');
    dateBox.value = today; dateBox.dispatchEvent(new Event('change'));
  }
  const descBox = /** @type {HTMLInputElement} */ (document.getElementById('rp-description'));
  descBox.value = defaultDesc;
  descBox.readOnly = !!_inst;
  document.getElementById('rp-is-continuity').value = isContinuity ? '1' : '';

  const errEl = document.getElementById('rp-error');
  if (errEl) { errEl.style.display = 'none'; errEl.textContent = ''; }
  const btn = document.getElementById('rp-submit-btn');
  if (btn) { btn.disabled = false; btn.textContent = actionWords(); }
  const title = document.getElementById('rp-title');
  if (title) title.textContent = actionWords();

  document.getElementById('rp-client-id').value = clientId;
}

function showWindow() {
  const overlay = document.getElementById('record-payment-overlay');
  if (!overlay) return;
  // The Change billing window's look, loaded the way the Billing card loads it (the card may not be on this account);
  // the window shows once it is there, so it never draws without its dialog box
  overlay.style.visibility = 'hidden';
  overlay.classList.remove('hidden');
  void cardModule().then((m) => m.ensureChangeBillingCss()).catch(() => {})
    .then(() => { overlay.style.visibility = ''; });
}

window.closeRecordPaymentModal = function() {
  _openings++;
  document.getElementById('record-payment-overlay').classList.add('hidden');
};

/**
 * Installments: the payment saved on the server by its id, once. Already recorded says so; a refusal stays in the window.
 * @param {{ payment: any, count: number, requestKey: string }} inst
 */
async function recordInstallment(inst) {
  const clientId = document.getElementById('rp-client-id').value;
  const date     = document.getElementById('rp-date').value;
  const errEl = document.getElementById('rp-error');
  const btn   = document.getElementById('rp-submit-btn');
  const showErr = (msg) => { if (errEl) { errEl.textContent = msg; errEl.style.display = ''; } };
  if (!date) return showErr('Please select the date payment was received.');
  if (date > _config.localToday()) return showErr('Pick the day they paid, today or earlier.');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  const words = `Payment ${inst.payment.n} of ${inst.count}`;
  try {
    const res = await callFunction('coach-billing-plan', { action: 'record-installment-payment', clientId, paymentId: inst.payment.id, paidOn: date, requestKey: inst.requestKey });
    window.closeRecordPaymentModal();
    const { tookAnswer, drawAgain } = await import('./new-purchase/purchase-button.js');
    const c = clientsList().find(x => x.id === clientId);
    if (c) tookAnswer(c, res);
    showToast(res && res.recorded === false ? `${words} was already recorded.` : `${words} recorded.`, 'success');
    await drawAgain(false);
  } catch (err) {
    showErr(/** @type {any} */ (err).message || 'Could not record it. Try again.');
    if (btn) { btn.disabled = false; btn.textContent = actionWords(); }
  }
}

window.handleRecordPayment = async function() {
  if (_inst) return recordInstallment(_inst);
  const clientId    = document.getElementById('rp-client-id').value;
  const amount      = parseFloat(document.getElementById('rp-amount').value);
  const date        = document.getElementById('rp-date').value;
  const description = document.getElementById('rp-description').value.trim();

  const errEl = document.getElementById('rp-error');
  const btn   = document.getElementById('rp-submit-btn');

  const showErr = (msg) => {
    if (errEl) { errEl.textContent = msg; errEl.style.display = ''; }
  };

  if (!amount || amount <= 0) return showErr('Please enter a valid payment amount.');
  if (!date)                  return showErr('Please select the date payment was received.');

  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }

  try {
    const c = clientsList().find(x => x.id === clientId);
    if (!c) throw new Error('Client not found.');

    const existingHistory  = Array.isArray(c.paymentHistory) ? c.paymentHistory : [];
    const isContinuity     = document.getElementById('rp-is-continuity')?.value === '1';
    // + Add Entry: a correction to the history, not a payment. Its row says so, and the server gives it no month.
    const isLog            = _mode === 'log' && !isContinuity;

    const source = isContinuity ? handSource(c) : (c.paymentSource || 'external');
    // Compute the period this payment covers
    let entryPeriod = '';
    let isLate = false;
    if (isContinuity) {
      const scheduledDue = getNextContinuityDueDate(c);
      entryPeriod = _deps.periodLabel(scheduledDue || date);
      // Mark as late if the payment date exceeds the grace period after the scheduled due date.
      // Stripe: no grace — if it failed and retried late, it's late.
      // Manual: 15-day grace period before flagging as late.
      if (scheduledDue) {
        const graceDays = source === 'stripe' ? 2 : 15;
        const cutoff = new Date(scheduledDue.getTime() + graceDays * 86400000);
        isLate = date > _config.localDateStr(cutoff);
      }
    } else if (c.billingStructure === 'installments') {
      const totalInst = parseInt(c.installmentCount) || 0;
      const paidSoFar = nextInstallmentNumber(existingHistory, c);
      // Review C fix 2: with only card payments open, a plain payment with no period, never one of them
      if (!_cardsOnly) entryPeriod = totalInst ? `Installment ${paidSoFar} of ${totalInst}` : `Installment ${paidSoFar}`;
    } else if (!_cardsOnly) {
      // MRR — period = the billing month of the payment date (fix-check C P15: never on the plain payment)
      entryPeriod = _deps.periodLabel(date);
    }

    const newEntry = {
      id:          crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
      date:        date,
      amount:      amount,
      description: description || (_cardsOnly ? 'Payment' : c.billingStructure === 'installments' ? 'Installment Payment' : 'MRR Payment'),
      period:      entryPeriod,
      isLate:      isLate || undefined,
      entry:       isLog ? 'log' : undefined,
      // Installments set up in Change billing (10/22): its month comes on schedule and none is asked for, so the daily
      // sessions check never reports it, even once the program has ended and the mark is gone
      monthOnSchedule: (!isContinuity && !isLog && installmentsByPlan(c)) || undefined,
      source,
      recordedAt:  new Date().toISOString(),
    };

    const newHistory       = [...existingHistory, newEntry];
    const currentCollected = Number(c.totalCollected ?? c.moneyCollectedUpfront) || 0;
    const newCollected     = currentCollected + amount;

    const updatePayload = { paymentHistory: newHistory, totalCollected: newCollected };
    if (isContinuity) {
      // Anchor to the SCHEDULED due date, not today's collection date.
      // This ensures paying a day early still advances to the correct next period.
      // e.g. collecting March 31 for an April 1 due date → stores April 1 → next due = May 1.
      const scheduledDue = getNextContinuityDueDate(c);
      const dueDateStr = scheduledDue ? _config.localDateStr(scheduledDue) : date;
      updatePayload.lastContinuityCollectedDate = dueDateStr;
      c.lastContinuityCollectedDate = dueDateStr; // optimistic
    }

    await updateClient(clientId, updatePayload);

    // ⚠️ ONE SESSIONS SYSTEM (part 7): where the switch is on, the server gives the month, from the row just written,
    // and the window waits for it so the coach is told what it added. Nothing for + Add Entry. A continuity payment asks
    // too (10/30): continuity paid by hand gives its month when it is recorded.
    // Nothing for installments set up in Change billing either (10/22): their months come on schedule.
    // ⚠️ A SWITCH THAT CANNOT BE READ (review 10/12): the payment is saved already, so it is not "off" (the page's old
    // write) and not an error that invites a second press: the month is "not added yet", with Try again.
    /** @type {boolean|null} */
    let ledgerOn = null;
    try { ledgerOn = await _sessions.sessionsLedgerOn(); }
    catch (err) { console.warn('[sessions] the switch could not be read:', err.message); }
    const paid = `Payment of ${formatCurrency(amount)} recorded. `;
    let sessionWords = null;
    /** @type {string|null} */
    let refusedWords = null;
    let monthMissing = false;
    const wantsMonth = asksForTheMonth(c, { continuity: isContinuity, log: isLog });
    if (ledgerOn === null && wantsMonth) monthMissing = true;
    if (ledgerOn === true && wantsMonth) {
      try { sessionWords = await giveTheMonth(c, date, newEntry.id, isContinuity); }
      catch (err) {
        console.warn('[sessions] the month on payment failed:', err.message);
        const words = lasting(err);
        if (words) refusedWords = `Its sessions were not added: ${words}`;
        else monthMissing = true;
      }
    }

    // Auto-issue session credits on manual payment for in-person/hybrid clients.
    // Period start = payment date; period end = payment date + 1 month − 1 day.
    // Continuity payments don't trigger issuance (they're for coaching retainer, not sessions).
    //
    // ⚠️ NOR DO INSTALLMENT PAYMENTS (2026-10-02). The daily job gives an
    // installment client every month of their program, counted from their billing
    // start date, so a batch for this payment's date as well would hand them the
    // month twice. Same reason stripe-webhook.js skips a paid installment invoice.
    if (ledgerOn === false && !isContinuity && c.billingStructure !== 'installments'
        && (c.serviceType === 'hybrid' || c.serviceType === 'in-person')) {
      _sessions.autoIssueForPaymentDate(c, _deps.getCurrentUser()?.id, date).catch(err =>
        console.warn('[sessions] auto-issue on payment failed:', err.message)
      );
    }

    window.closeRecordPaymentModal();
    _deps.renderManualContinuity();
    renderCurrentView(); // re-render profile (or whatever view is active) with optimistic data
    if (monthMissing) offerTryAgain(paid, c, date, newEntry.id, isContinuity);
    else if (refusedWords) showToast(`${paid}${refusedWords}`, 'warning');
    else showToast(sessionWords ? `${paid}${sessionWords}` : paid.trim(), 'success');
  } catch (err) {
    showErr(err.message || 'Failed to record payment. Please try again.');
    if (btn) { btn.disabled = false; btn.textContent = actionWords(); }
  }
};
